import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { MessageSquare, LayoutDashboard, ShieldCheck, LogOut, UserCircle } from "lucide-react";

const BridgeMark = ({ className }) => (
  <svg viewBox="0 0 36 28" fill="none" className={className} aria-hidden="true">
    <rect x="1.5" y="13" width="3" height="14" rx="1.5" fill="currentColor" />
    <rect x="31.5" y="13" width="3" height="14" rx="1.5" fill="currentColor" />
    <path d="M3 13 Q18 1 33 13" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    <line x1="0" y1="21" x2="36" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="10.5" y1="7" x2="10.5" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="18" y1="3" x2="18" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="25.5" y1="7" x2="25.5" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

export default function TopBar({ user }) {
  const navigate = useNavigate();
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/login");
  };

  const navItems = [
    { to: "/dashboard", label: "Sessions", icon: LayoutDashboard, testId: "nav-dashboard" },
    { to: "/chatbot", label: "Assistant", icon: MessageSquare, testId: "nav-chatbot" },
    ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", icon: ShieldCheck, testId: "nav-admin" }] : []),
    ...(user?.role === "teacher" ? [{ to: "/profile", label: "Profile", icon: UserCircle, testId: "nav-profile" }] : []),
  ];

  return (
    <motion.header
      initial={{ y: -8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="sticky top-0 z-40 flex items-center justify-between border-b border-[#E8D5C8]/60 bg-[#FDF3EC]/95 px-5 py-3.5 backdrop-blur-sm md:px-8"
    >
      <Link to="/dashboard" data-testid="topbar-brand" className="flex items-center gap-2 text-[#E8542A] transition-opacity hover:opacity-80">
        <BridgeMark className="h-5 w-7" />
        <span className="font-display text-lg font-bold">ClassBridge</span>
      </Link>

      <nav className="flex items-center gap-1">
        {navItems.map(({ to, label, icon: Icon, testId }) => (
          <Link
            key={to}
            to={to}
            data-testid={testId}
            className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium text-[#8A6D5C] transition-all duration-150 hover:bg-[#E8542A]/8 hover:text-[#E8542A]"
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{label}</span>
          </Link>
        ))}
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="ml-1 flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium text-[#8A6D5C] transition-all duration-150 hover:bg-red-50 hover:text-red-500"
        >
          <LogOut className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Log out</span>
        </button>
      </nav>
    </motion.header>
  );
}
